import React from "react";
import { WHATSAPP_URL } from "../data/content";
import "./CartDrawer.css";

function parsePrice(price) {
  if (typeof price === "number") return price;
  if (!price) return 0;
  const asStr = String(price).replace(/,/g, "");
  const match = asStr.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function formatCurrency(value) {
  return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function getItemPrice(product) {
  if (!product) return 0;
  if (typeof product.price === "number") return product.price;
  // support new field priceDiscount if present
  if (product.priceDiscount && typeof product.priceDiscount === "number")
    return product.priceDiscount;
  return parsePrice(product.price);
}

export default function CartDrawer({
  open,
  cartItems,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onContinueToOrder,
}) {
  const subtotal = cartItems.reduce(
    (sum, item) => sum + getItemPrice(item.product) * item.quantity,
    0,
  );

  const messageLines = cartItems.map((item) => {
    const product = item.product || {};
    const details = [product.title, product.subtitle, product.packSize]
      .filter(Boolean)
      .join(" • ");

    return `${item.quantity} x ${details} = ${formatCurrency(
      getItemPrice(product) * item.quantity,
    )}`;
  });

  const message = encodeURIComponent(
    `Hi! I would like to order the following items from Swathi's Wellness:\n\n` +
      `${messageLines.join("\n")}\n\nSubtotal: ${formatCurrency(subtotal)}\n\nPlease share payment & delivery details.`,
  );

  return (
    <div className={`cart-drawer ${open ? "cart-drawer--open" : ""}`}>
      <div className="cart-drawer__backdrop" onClick={onClose} />
      <aside className="cart-drawer__panel" aria-hidden={!open}>
        <div className="cart-drawer__header">
          <div>
            <p className="cart-drawer__eyebrow">Your Cart</p>
            <h3 className="cart-drawer__title">Cart Summary</h3>
          </div>
          <button
            className="cart-drawer__close"
            onClick={onClose}
            aria-label="Close cart"
          >
            ×
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-drawer__empty">
            <p>Your cart is empty. Add a product to view it here.</p>
          </div>
        ) : (
          <div className="cart-drawer__content">
            <ul className="cart-drawer__items">
              {cartItems.map((item) => (
                <li key={item.product.id} className="cart-drawer__item">
                  <div>
                    <div className="cart-drawer__item-title">
                      {item.product.title}
                    </div>
                    <div className="cart-drawer__item-meta">
                      <span>{formatCurrency(getItemPrice(item.product))}</span>
                      <span>Qty: {item.quantity}</span>
                    </div>
                  </div>
                  <div className="cart-drawer__item-actions">
                    <div className="cart-drawer__quantity-controls">
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateQuantity(item.product.id, item.quantity - 1)
                        }
                        aria-label={`Decrease quantity for ${item.product.title}`}
                      >
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateQuantity(item.product.id, item.quantity + 1)
                        }
                        aria-label={`Increase quantity for ${item.product.title}`}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className="cart-drawer__remove"
                      onClick={() => onRemoveItem(item.product.id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="cart-drawer__summary">
              <div className="cart-drawer__summary-row">
                <span>Subtotal</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              <p className="cart-drawer__note">
                Ready to place your order? Continue to the order section or
                complete it instantly via WhatsApp.
              </p>
            </div>

            <div className="cart-drawer__actions">
              <button
                className="cart-drawer__button cart-drawer__button--secondary"
                onClick={onContinueToOrder}
              >
                Continue to Order
              </button>
              <a
                className="cart-drawer__button cart-drawer__button--primary"
                href={`${WHATSAPP_URL}?text=${message}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onCheckout?.({ items: cartItems })}
              >
                Checkout on WhatsApp
              </a>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
